/**
 * Trade Loop v2.0 - Main App Component
 *
 * Uses React Router DOM for routing with cookie-based authentication
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { isNative } from '@/utils/platform';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { AdminRoute } from './routes/AdminRoute';
import ScrollToTop from './components/ScrollToTop';
import { SessionExpirationHandler } from './components/auth/SessionExpirationHandler';
import { SyncStatus } from './components/SyncStatus';
import { PushNotificationPrompt } from './components/PushNotificationPrompt';
import { UpdateBanner } from './components/UpdateBanner';
import { StoreUpdateBanner } from './components/StoreUpdateBanner';
import { StoreUpdateRequiredModal } from './components/StoreUpdateRequiredModal';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { EmailUndeliverableBanner } from './components/EmailUndeliverableBanner';
import InitialLoginOnboardingModal from './components/auth/InitialLoginOnboardingModal';
import { GettingStartedProvider } from './components/getting-started/GettingStartedContext';
import GettingStartedTour from './components/getting-started/GettingStartedTour';
import { useAuth } from './contexts/AuthContext';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupRedirect from './routes/SignupRedirect';
import AccountDeletionInfoPage from './pages/AccountDeletionInfoPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AccountLockedPage from './pages/AccountLockedPage';
import ContactUsPage from './pages/ContactUsPage';
import FAQPage from './pages/FAQPage';
import PlansPage from './pages/PlansPage';
import ProfileSettingsPage from './pages/ProfileSettingsPage';
import AccountPage from './pages/AccountPage';
import EditProfilePage from './pages/EditProfilePage';
import NetworkPage from './pages/NetworkPage';
import DashboardPage from './pages/DashboardPage';
import ReferralsPage from './pages/ReferralsPage';
import AvailabilityRequestPage from './pages/AvailabilityRequestPage';
import SendReferralPage from './pages/SendReferralPage';
import NotificationsPage from './pages/NotificationsPage';
import QRPage from './pages/QRPage';
import ConnectPage from './pages/ConnectPage';
import MessagesPage from './pages/MessagesPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import LegalPage from './pages/LegalPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentMethodsPage from './pages/PaymentMethodsPage';
import AdminServicesPage from './pages/AdminServicesPage';
import PushDiagnosticsPage from './pages/PushDiagnosticsPage';
import AdminStoreUpdatesPage from './pages/AdminStoreUpdatesPage';
import OrganizationPage from './pages/OrganizationPage';
import OrganizationPausedPage from './pages/OrganizationPausedPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import ReferralInvitePage from './pages/ReferralInvitePage';
import BlockedUsersPage from './pages/BlockedUsersPage';
import MaintenancePage from './pages/MaintenancePage';
import { useAndroidBackButton } from './hooks/useAndroidBackButton';
import { usePushNavigation } from './hooks/usePushNavigation';
import { useEntitlementRefreshOnResume } from './hooks/useEntitlementRefreshOnResume';
import { useOtaAppReady } from './hooks/useOtaAppReady';
import { useDeviceTimezoneSync } from './hooks/useDeviceTimezoneSync';

const NotFoundPage = () => <div className="p-8 text-center">404 - Page Not Found</div>;

/**
 * Renders the first-login onboarding modal on top of whatever route is
 * showing whenever an authenticated user still has
 * `initialOnboardingCompleted === false`. Kept as its own component so
 * `useAuth()` can be called under the AuthProvider without pulling all of
 * App's other hooks into that dependency.
 */
function InitialOnboardingGate() {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading || !isAuthenticated || !user) return null;
  if (user.initialOnboardingCompleted) return null;
  return <InitialLoginOnboardingModal />;
}

function App() {
  // Android hardware/gesture back. Must run before the maintenance-mode early
  // return below so the hook order stays stable. No-op on web.
  useAndroidBackButton();

  // Hands the router to the push service so notification taps land on the right screen,
  // including a killed-state tap that fired before this component existed. Same
  // constraint: must run before the early return below.
  usePushNavigation();

  // Card 18: a native user subscribes in a browser, outside the app, so the plan they come back
  // holding is stale. Refetches the profile on resume. Same early-return constraint as above.
  useEntitlementRefreshOnResume();

  // Card 13: confirm this OTA bundle booted, once React has mounted AND the session has resolved.
  // Miss it and the plugin auto-reverts every bundle ~20s after launch. Placed BEFORE the
  // maintenance-mode early return below for the usual hook-order reason, and because a bundle
  // shipped during a maintenance window is still a healthy bundle — reverting it would strand
  // devices on the pre-maintenance build.
  useOtaAppReady();

  // Quietly saves the device's timezone on login/app open so reminders go out in the user's
  // local time. No UI. Same early-return constraint as above.
  useDeviceTimezoneSync();

  // Migration freeze: when the site is taken offline for the Supabase
  // US-East → US-West cutover, ship the build with VITE_MAINTENANCE_MODE=true so
  // the web client shows a maintenance notice instead of a broken app. The
  // backend MAINTENANCE_MODE env is the authoritative write-freeze.
  if (import.meta.env.VITE_MAINTENANCE_MODE === 'true') {
    return <MaintenancePage />;
  }

  return (
    <GettingStartedProvider>
      <ScrollToTop />
      <SessionExpirationHandler />
      <SyncStatus />
      <PushNotificationPrompt />
      <UpdateBanner />
      <StoreUpdateBanner />
      <StoreUpdateRequiredModal />
      <ImpersonationBanner />
      <EmailUndeliverableBanner />
      <InitialOnboardingGate />
      <GettingStartedTour />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Legacy path — links are already in users' hands. See SignupRedirect. */}
      <Route path="/signup" element={<SignupRedirect />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/account-locked" element={<AccountLockedPage />} />
      <Route path="/contact-us" element={<ContactUsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      {/* PUBLIC on purpose — this is the "Delete account URL" published on the Play store
          listing (Card 12). It must be readable by someone who has uninstalled the app or
          cannot get into their account, which is exactly who follows that link. It only
          EXPLAINS; /delete-account below stays behind ProtectedRoute because performing the
          deletion requires the account holder to sign in and re-enter their password. */}
      <Route path="/account-deletion" element={<AccountDeletionInfoPage />} />
      <Route path="/plans" element={<PlansPage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/payment-cancel" element={<PaymentCancelPage />} />
      <Route path="/connect/:userId" element={<ConnectPage />} />
      <Route path="/accept-invite/:inviteCode" element={<AcceptInvitePage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/invite/:token" element={<ReferralInvitePage />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <ReferralsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/availability-requests/:id"
        element={
          <ProtectedRoute>
            <AvailabilityRequestPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/network"
        element={
          <ProtectedRoute>
            <NetworkPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <ProfileSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfileSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      {/* Card 12 — the route is CLOSED on native, not just the button that reaches it.
          PaymentMethodsPage renders a Stripe CardElement and calls confirmCardSetup, so it takes a
          card inside the app: a Google Play Billing violation, and Apple Guideline 3.1.1. Card 18
          guarded the checkout paths and missed this one. Billing lives on gotradeloop.com.

          Guarded HERE as well as at the entry point because a route survives deep links, the OTA
          bundle's own history, and any future caller — hiding one button is not a control. */}
      <Route
        path="/payment-methods"
        element={
          isNative ? (
            <Navigate to="/account" replace />
          ) : (
            <ProtectedRoute>
              <PaymentMethodsPage />
            </ProtectedRoute>
          )
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        }
      />
      <Route path="/legal" element={<LegalPage />} />
      <Route
        path="/edit-profile"
        element={
          <ProtectedRoute>
            <EditProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/send-referral"
        element={
          <ProtectedRoute>
            <SendReferralPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/qr"
        element={
          <ProtectedRoute>
            <QRPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/delete-account"
        element={
          <ProtectedRoute>
            <DeleteAccountPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminServicesPage />
          </ProtectedRoute>
        }
      />
      {/*
        Push diagnostics (Card 17). Admin-gated on the route AND on the API — the client
        gate hides the screen, the server gate protects the data.
      */}
      <Route
        path="/admin/push-diagnostics"
        element={
          <AdminRoute>
            <PushDiagnosticsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/store-updates"
        element={
          <AdminRoute>
            <AdminStoreUpdatesPage />
          </AdminRoute>
        }
      />
      <Route
        path="/organization"
        element={
          <ProtectedRoute>
            <OrganizationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organization-paused"
        element={
          <ProtectedRoute>
            <OrganizationPausedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/blocked-users"
        element={
          <ProtectedRoute>
            <BlockedUsersPage />
          </ProtectedRoute>
        }
      />

      {/* 404 Catch-all */}
      <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </GettingStartedProvider>
  );
}

export default App;